import * as React from 'react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';

import Alert from '@mui/material/Alert';
import Autocomplete from '@mui/material/Autocomplete';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Checkbox from '@mui/material/Checkbox';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import CssBaseline from '@mui/material/CssBaseline';
import Divider from '@mui/material/Divider';
import FormControl from '@mui/material/FormControl';
import FormControlLabel from '@mui/material/FormControlLabel';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import Paper from '@mui/material/Paper';
import Select from '@mui/material/Select';
import Stack from '@mui/material/Stack';
import Switch from '@mui/material/Switch';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { alpha } from '@mui/material/styles';
import ArrowBackRoundedIcon from '@mui/icons-material/ArrowBackRounded';
import RefreshRoundedIcon from '@mui/icons-material/RefreshRounded';
import SensorsRoundedIcon from '@mui/icons-material/SensorsRounded';
import TimelineRoundedIcon from '@mui/icons-material/TimelineRounded';
import {
  Area,
  AreaChart,
  CartesianGrid,
  Legend,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';

import AppNavbar from '../components/AppNavbar';
import Header from '../components/Header';
import SideMenu from '../components/SideMenu';
import AppTheme from '../shared-theme/AppTheme';
import axiosInstance from '../utils/axiosInstance';

import {
  chartsCustomizations,
  dataGridCustomizations,
  datePickersCustomizations,
  treeViewCustomizations,
} from './theme/customizations';

const xThemeComponents = {
  ...chartsCustomizations,
  ...dataGridCustomizations,
  ...datePickersCustomizations,
  ...treeViewCustomizations,
};

const FIELD_COLORS = ['#2563eb', '#16a34a', '#ea580c', '#7c3aed', '#0891b2', '#dc2626', '#ca8a04'];
const AUTO_REFRESH_MS = 15000;
const MAX_PLOTTED_FIELDS = FIELD_COLORS.length;

function rangeToDates(range) {
  const now = new Date();
  const hours = Number(range);
  const from = new Date(now.getTime() - hours * 60 * 60 * 1000);
  return { from: from.toISOString(), to: now.toISOString() };
}

function formatLabel(value) {
  return String(value || '')
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (char) => char.toUpperCase());
}

function formatValue(value) {
  if (value === null || value === undefined || value === '') {
    return 'N/A';
  }
  if (typeof value === 'boolean') {
    return value ? 'Yes' : 'No';
  }
  if (typeof value === 'number') {
    return Number.isInteger(value) ? String(value) : value.toLocaleString(undefined, { maximumFractionDigits: 4 });
  }
  if (typeof value === 'object') {
    return JSON.stringify(value);
  }
  return String(value);
}

function formatDateTime(value) {
  if (!value) {
    return 'N/A';
  }
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? String(value) : parsed.toLocaleString();
}

function formatTimeTick(value) {
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) {
    return value;
  }
  return parsed.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
}

function coerceNumber(value) {
  if (typeof value === 'number' && Number.isFinite(value)) {
    return value;
  }
  if (typeof value !== 'string') {
    return null;
  }
  const trimmed = value.trim();
  if (!trimmed || trimmed.toLowerCase() === 'nan') {
    return null;
  }
  const normalized = trimmed.replace(/,/g, '');
  if (!/^[-+]?(?:\d+\.?\d*|\.\d+)(?:e[-+]?\d+)?$/i.test(normalized)) {
    return null;
  }
  const parsed = Number(normalized);
  return Number.isFinite(parsed) ? parsed : null;
}

function isTelemetryValueField(value) {
  const field = String(value || '').trim().toLowerCase();
  return Boolean(field) && field !== 'ts';
}

function statusColor(value) {
  if (['online', 'running', 'available', 'normal'].includes(value)) {
    return 'success';
  }
  if (['degraded', 'paused', 'busy', 'warning', 'reserved'].includes(value)) {
    return 'warning';
  }
  if (['offline', 'faulted', 'stopped', 'critical', 'unavailable'].includes(value)) {
    return 'error';
  }
  return 'default';
}

function MetricTile({ label, value, sublabel, color, trend }) {
  return (
    <Paper
      variant="outlined"
      sx={{
        p: 2,
        minHeight: 128,
        borderColor: (theme) => alpha(color || theme.palette.primary.main, 0.24),
        bgcolor: (theme) => alpha(color || theme.palette.primary.main, 0.055),
      }}
    >
      <Stack spacing={1}>
        <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={1}>
          <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700 }}>
            {label}
          </Typography>
          {trend ? <Chip size="small" label={trend} variant="outlined" /> : null}
        </Stack>
        <Typography variant="h5" sx={{ fontWeight: 800, lineHeight: 1.15 }}>
          {value}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          {sublabel || 'Latest sample'}
        </Typography>
      </Stack>
    </Paper>
  );
}

export default function AssetRealtimeDashboard(props) {
  const { assetId } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const telemetrySubscriptionId = searchParams.get('telemetry_subscription_id') || '';
  const [rangeHours, setRangeHours] = useState('1');
  const [selectedFields, setSelectedFields] = useState([]);
  const [fieldSelectionInitialized, setFieldSelectionInitialized] = useState(false);
  const [numericFieldCatalog, setNumericFieldCatalog] = useState([]);
  const [autoRefresh, setAutoRefresh] = useState(false);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [state, setState] = useState({ loading: true, error: '', data: null });
  const latestRequestId = useRef(0);

  async function loadTelemetry(nextRange = rangeHours, options = {}) {
    const requestId = latestRequestId.current + 1;
    latestRequestId.current = requestId;
    if (!options.silent) {
      setState((current) => ({ ...current, loading: true, error: '' }));
    }
    const { from, to } = rangeToDates(nextRange);
    try {
      const endpoint = telemetrySubscriptionId
        ? `/telemetry-subscriptions/${encodeURIComponent(telemetrySubscriptionId)}/telemetry`
        : `/assets/${assetId}/telemetry`;
      const response = await axiosInstance.get(endpoint, {
        params: {
          from,
          to,
          limit: 10000,
        },
      });
      if (requestId !== latestRequestId.current) {
        return;
      }
      setState({ loading: false, error: '', data: response.data });
      setLastUpdated(new Date());
    } catch (error) {
      if (requestId !== latestRequestId.current) {
        return;
      }
      setState((current) => ({
        loading: false,
        error: error?.response?.data?.message || 'Unable to query telemetry.',
        data: options.silent ? current.data : null,
      }));
    }
  }

  useEffect(() => {
    setSelectedFields([]);
    setFieldSelectionInitialized(false);
    setNumericFieldCatalog([]);
    setState({ loading: true, error: '', data: null });
    loadTelemetry(rangeHours);
  }, [assetId, telemetrySubscriptionId]);

  useEffect(() => {
    if (!autoRefresh) {
      return undefined;
    }
    const timer = window.setInterval(() => {
      loadTelemetry(rangeHours, { silent: true });
    }, AUTO_REFRESH_MS);
    return () => window.clearInterval(timer);
  }, [autoRefresh, rangeHours, assetId, telemetrySubscriptionId]);

  const asset = state.data?.asset;
  const telemetry = state.data?.telemetry;
  const rawRows = telemetry?.rows || [];

  const normalizedRows = useMemo(() => (
    rawRows
      .filter((row) => row?.field && isTelemetryValueField(row.field))
      .map((row) => {
        const numericValue = coerceNumber(row.value);
        return {
          ...row,
          field: String(row.field),
          numericValue,
          isNumeric: numericValue !== null,
        };
      })
  ), [rawRows]);

  const availableFields = useMemo(() => {
    const fromResponse = Array.isArray(telemetry?.fields) ? telemetry.fields : [];
    const fromRows = normalizedRows.map((row) => row.field);
    return [...new Set([...fromResponse, ...fromRows].filter(isTelemetryValueField))].sort();
  }, [telemetry, normalizedRows]);

  const numericFieldsInResponse = useMemo(() => (
    [...new Set(normalizedRows.filter((row) => row.isNumeric).map((row) => row.field))].sort()
  ), [normalizedRows]);

  useEffect(() => {
    if (!numericFieldsInResponse.length) {
      return;
    }
    setNumericFieldCatalog((current) => (
      [...new Set([...current, ...numericFieldsInResponse])].sort()
    ));
  }, [numericFieldsInResponse]);

  useEffect(() => {
    if (fieldSelectionInitialized || !numericFieldsInResponse.length) {
      return;
    }
    setSelectedFields(numericFieldsInResponse.slice(0, Math.min(6, MAX_PLOTTED_FIELDS)));
    setFieldSelectionInitialized(true);
  }, [numericFieldsInResponse, fieldSelectionInitialized]);

  const visibleRows = useMemo(() => (
    normalizedRows.filter((row) => selectedFields.includes(row.field))
  ), [normalizedRows, selectedFields]);

  const numericFields = useMemo(() => (
    [...new Set(visibleRows.filter((row) => row.isNumeric).map((row) => row.field))].sort()
  ), [visibleRows]);

  const nonNumericRows = useMemo(() => (
    normalizedRows.filter((row) => !row.isNumeric)
  ), [normalizedRows]);

  const latestByField = useMemo(() => {
    const latest = new Map();
    visibleRows.forEach((row) => latest.set(row.field, row));
    return latest;
  }, [visibleRows]);

  const latestByAvailableField = useMemo(() => {
    const latest = new Map();
    normalizedRows.forEach((row) => latest.set(row.field, row));
    return latest;
  }, [normalizedRows]);

  const latestNumericTiles = useMemo(() => (
    numericFields.slice(0, 6).map((field, index) => {
      const row = latestByField.get(field);
      return {
        field,
        color: FIELD_COLORS[index % FIELD_COLORS.length],
        value: row ? formatValue(row.numericValue) : 'N/A',
        time: row?.time,
      };
    })
  ), [numericFields, latestByField]);

  const chartData = useMemo(() => {
    const byTime = new Map();
    visibleRows
      .filter((row) => row.isNumeric)
      .forEach((row) => {
        const item = byTime.get(row.time) || {
          time: row.time,
          label: formatTimeTick(row.time),
        };
        item[row.field] = row.numericValue;
        byTime.set(row.time, item);
      });
    return Array.from(byTime.values()).sort((left, right) => new Date(left.time) - new Date(right.time));
  }, [visibleRows]);

  const handleRangeChange = (event) => {
    const nextRange = event.target.value;
    setRangeHours(nextRange);
    loadTelemetry(nextRange);
  };

  const handleFieldChange = (_event, nextFields) => {
    setFieldSelectionInitialized(true);
    setSelectedFields(nextFields.slice(0, MAX_PLOTTED_FIELDS));
  };

  return (
    <AppTheme {...props} themeComponents={xThemeComponents}>
      <CssBaseline enableColorScheme />
      <Box sx={{ display: 'flex' }}>
        <SideMenu />
        <AppNavbar />
        <Box component="main" sx={{ flexGrow: 1, p: 3 }}>
          <Header
            breadcrumbItems={[
              { label: 'AMX xConsole', href: '/' },
              { label: 'My Capabilities', href: '/my-capabilities' },
              { label: asset?.asset_name || 'Asset Dashboard' },
            ]}
          />

          <Stack spacing={3}>
            <Paper
              variant="outlined"
              sx={{
                p: { xs: 2, md: 3 },
                overflow: 'hidden',
                bgcolor: (theme) => alpha(theme.palette.background.paper, 0.94),
                borderColor: (theme) => alpha(theme.palette.primary.main, 0.18),
              }}
            >
              <Stack direction={{ xs: 'column', lg: 'row' }} spacing={2.5} justifyContent="space-between">
                <Stack spacing={1.5}>
                  <Stack direction="row" spacing={1.25} alignItems="center">
                    <SensorsRoundedIcon color="primary" />
                    <Typography variant="h4" sx={{ fontWeight: 800 }}>
                      Asset Real Time Dashboard
                    </Typography>
                  </Stack>
                  <Typography variant="body2" color="text.secondary">
                    {asset?.asset_name || assetId}
                  </Typography>
                  <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                    <Chip label={asset?.asset_type_display_name || formatLabel(asset?.asset_type)} />
                    <Chip color={statusColor(asset?.connection_status)} label={asset?.connection_status || 'unknown'} />
                    <Chip color={statusColor(asset?.health_status)} label={`Health: ${asset?.health_status || 'unknown'}`} variant="outlined" />
                    <Chip label={`${visibleRows.length} samples`} variant="outlined" />
                    <Chip label={`${numericFields.length} numeric fields`} variant="outlined" />
                  </Stack>
                </Stack>
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} alignItems={{ xs: 'stretch', sm: 'center' }}>
                  <Button variant="outlined" startIcon={<ArrowBackRoundedIcon />} onClick={() => navigate(`/assets/${assetId}`)}>
                    Asset Page
                  </Button>
                  <Button variant="contained" startIcon={<RefreshRoundedIcon />} onClick={() => loadTelemetry()}>
                    Refresh
                  </Button>
                </Stack>
              </Stack>
            </Paper>

            <Paper variant="outlined" sx={{ p: 2 }}>
              <Stack direction={{ xs: 'column', lg: 'row' }} spacing={2} alignItems={{ xs: 'stretch', lg: 'center' }}>
                <FormControl size="small" sx={{ minWidth: { xs: '100%', lg: 180 } }}>
                  <InputLabel id="range-label">Time Range</InputLabel>
                  <Select labelId="range-label" value={rangeHours} label="Time Range" onChange={handleRangeChange}>
                    <MenuItem value="1">Last hour</MenuItem>
                    <MenuItem value="6">Last 6 hours</MenuItem>
                    <MenuItem value="24">Last 24 hours</MenuItem>
                    <MenuItem value="168">Last 7 days</MenuItem>
                  </Select>
                </FormControl>
                <Autocomplete
                  multiple
                  disableCloseOnSelect
                  limitTags={3}
                  options={numericFieldCatalog}
                  value={selectedFields}
                  onChange={handleFieldChange}
                  getOptionLabel={formatLabel}
                  getOptionDisabled={(option) => (
                    selectedFields.length >= MAX_PLOTTED_FIELDS && !selectedFields.includes(option)
                  )}
                  renderOption={(optionProps, option, { selected }) => (
                    <li {...optionProps}>
                      <Checkbox checked={selected} sx={{ mr: 1 }} />
                      {formatLabel(option)}
                    </li>
                  )}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Telemetry to plot"
                      placeholder={selectedFields.length ? '' : 'Choose telemetry'}
                      helperText={`Search and select up to ${MAX_PLOTTED_FIELDS} numeric fields`}
                    />
                  )}
                  size="small"
                  sx={{ flex: 1, minWidth: 280 }}
                />
                <FormControlLabel
                  control={
                    <Switch
                      checked={autoRefresh}
                      onChange={(event) => setAutoRefresh(event.target.checked)}
                    />
                  }
                  label="Auto refresh"
                  sx={{ m: 0, minWidth: 148 }}
                />
                <Typography variant="caption" color="text.secondary" sx={{ minWidth: 210 }}>
                  Updated {lastUpdated ? lastUpdated.toLocaleTimeString() : 'N/A'}
                </Typography>
              </Stack>
            </Paper>

            {state.loading ? (
              <Stack direction="row" spacing={2} alignItems="center">
                <CircularProgress size={24} />
                <Typography>Querying telemetry...</Typography>
              </Stack>
            ) : state.error ? (
              <Alert severity="error">{state.error}</Alert>
            ) : !telemetry?.configured ? (
              <Alert severity="info">{telemetry?.message || 'Telemetry is not configured yet.'}</Alert>
            ) : (
              <Stack spacing={3}>
                <Box
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: {
                      xs: '1fr',
                      sm: 'repeat(2, minmax(0, 1fr))',
                      xl: 'repeat(3, minmax(0, 1fr))',
                    },
                    gap: 2,
                  }}
                >
                  {latestNumericTiles.length ? (
                    latestNumericTiles.map((item) => (
                      <MetricTile
                        key={item.field}
                        label={formatLabel(item.field)}
                        value={item.value}
                        sublabel={formatDateTime(item.time)}
                        color={item.color}
                      />
                    ))
                  ) : (
                    <MetricTile
                      label="Numeric Telemetry"
                      value="No samples"
                      sublabel="No numeric values in this range"
                      color="#64748b"
                    />
                  )}
                </Box>

                <Paper
                  variant="outlined"
                  sx={{
                    p: 2,
                    borderColor: (theme) => alpha(theme.palette.primary.main, 0.18),
                    bgcolor: 'background.paper',
                  }}
                >
                  <Stack direction={{ xs: 'column', md: 'row' }} justifyContent="space-between" spacing={2} sx={{ mb: 2 }}>
                    <Stack direction="row" spacing={1} alignItems="center">
                      <TimelineRoundedIcon color="primary" />
                      <Typography variant="h6" sx={{ fontWeight: 800 }}>
                        Telemetry Trends
                      </Typography>
                    </Stack>
                    <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                      {numericFields.slice(0, 7).map((field, index) => (
                        <Chip
                          key={field}
                          size="small"
                          label={formatLabel(field)}
                          sx={{
                            borderColor: FIELD_COLORS[index % FIELD_COLORS.length],
                            color: FIELD_COLORS[index % FIELD_COLORS.length],
                          }}
                          variant="outlined"
                        />
                      ))}
                    </Stack>
                  </Stack>

                  <Divider sx={{ mb: 2 }} />

                  {chartData.length && numericFields.length ? (
                    <Box sx={{ width: '100%', height: { xs: 360, lg: 520 } }}>
                      <ResponsiveContainer width="100%" height="100%">
                        <AreaChart data={chartData} margin={{ top: 20, right: 28, left: 0, bottom: 8 }}>
                          <defs>
                            {numericFields.slice(0, 7).map((field, index) => {
                              const color = FIELD_COLORS[index % FIELD_COLORS.length];
                              const gradientId = `field-${index}`;
                              return (
                                <linearGradient key={field} id={gradientId} x1="0" y1="0" x2="0" y2="1">
                                  <stop offset="5%" stopColor={color} stopOpacity={0.24} />
                                  <stop offset="95%" stopColor={color} stopOpacity={0.02} />
                                </linearGradient>
                              );
                            })}
                          </defs>
                          <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#d6dde8" />
                          <XAxis
                            dataKey="time"
                            tickFormatter={formatTimeTick}
                            minTickGap={28}
                            tick={{ fontSize: 12 }}
                          />
                          <YAxis tick={{ fontSize: 12 }} width={56} />
                          <Tooltip
                            labelFormatter={(label) => formatDateTime(label)}
                            formatter={(value, name) => [formatValue(value), formatLabel(name)]}
                          />
                          <Legend formatter={formatLabel} />
                          {numericFields.slice(0, 7).map((field, index) => {
                            const color = FIELD_COLORS[index % FIELD_COLORS.length];
                            return (
                              <Area
                                key={field}
                                type="monotone"
                                dataKey={field}
                                name={field}
                                stroke={color}
                                fill={`url(#field-${index})`}
                                strokeWidth={2.5}
                                dot={chartData.length <= 20}
                                activeDot={{ r: 5 }}
                                connectNulls
                                isAnimationActive={false}
                              />
                            );
                          })}
                        </AreaChart>
                      </ResponsiveContainer>
                    </Box>
                  ) : (
                    <Box sx={{ minHeight: 260, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <Typography variant="body2" color="text.secondary">
                        No numeric telemetry was returned for this range.
                      </Typography>
                    </Box>
                  )}
                </Paper>

                <Box
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: { xs: '1fr', lg: '1fr 1fr' },
                    gap: 3,
                  }}
                >
                  <Paper variant="outlined" sx={{ overflow: 'hidden' }}>
                    <Box sx={{ px: 2, py: 1.25, bgcolor: 'background.default' }}>
                      <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                        Latest Field Values
                      </Typography>
                    </Box>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>Field</TableCell>
                          <TableCell>Value</TableCell>
                          <TableCell>Type</TableCell>
                          <TableCell>Time</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {availableFields.map((field) => {
                          const row = latestByAvailableField.get(field);
                          return (
                            <TableRow key={field} hover>
                              <TableCell sx={{ fontWeight: 700 }}>{formatLabel(field)}</TableCell>
                              <TableCell>{formatValue(row?.isNumeric ? row.numericValue : row?.value)}</TableCell>
                              <TableCell>{row?.isNumeric ? 'Number' : 'String'}</TableCell>
                              <TableCell>{formatDateTime(row?.time)}</TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </Paper>

                  <Paper variant="outlined" sx={{ overflow: 'hidden' }}>
                    <Box sx={{ px: 2, py: 1.25, bgcolor: 'background.default' }}>
                      <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                        String And State Telemetry
                      </Typography>
                    </Box>
                    {nonNumericRows.length ? (
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell>Time</TableCell>
                            <TableCell>Field</TableCell>
                            <TableCell>Value</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {nonNumericRows.slice(-200).map((row, index) => (
                            <TableRow key={`${row.time}-${row.field}-${index}`} hover>
                              <TableCell>{formatDateTime(row.time)}</TableCell>
                              <TableCell sx={{ fontWeight: 700 }}>{formatLabel(row.field)}</TableCell>
                              <TableCell>{formatValue(row.value)}</TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    ) : (
                      <Box sx={{ p: 2 }}>
                        <Typography variant="body2" color="text.secondary">
                          No string telemetry was returned for this range.
                        </Typography>
                      </Box>
                    )}
                  </Paper>
                </Box>
              </Stack>
            )}
          </Stack>
        </Box>
      </Box>
    </AppTheme>
  );
}
